import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";

const RELOAD_KEY = "gn_chunk_reload_at";
const RELOAD_COOLDOWN_MS = 10_000;

const isChunkLoadError = (error: unknown) => {
  const message = String(
    error instanceof Error ? error.message : error ?? ""
  );

  return (
    message.includes("Importing a module script failed") ||
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("Load failed") ||
    message.includes("dynamically imported module")
  );
};

const reloadWithFreshAssets = () => {
  const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
  if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;

  sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  const url = new URL(window.location.href);
  url.searchParams.set("v", String(Date.now()));
  window.location.replace(url.toString());
  return true;
};

type Props = { children: ReactNode };
type State = { hasError: boolean };

export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, _errorInfo: ErrorInfo) {
    if (isChunkLoadError(error)) {
      reloadWithFreshAssets();
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <section className="min-h-[60vh] flex items-center justify-center px-4 py-16">
        <div className="max-w-md text-center space-y-4">
          <h1 className="font-display text-2xl font-bold text-foreground">
            Refresh needed
          </h1>
          <p className="text-muted-foreground">
            A new version of GatorNotes is available. Refresh to continue.
          </p>
          <Button onClick={reloadWithFreshAssets}>Refresh</Button>
        </div>
      </section>
    );
  }
}
